import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom'; // New: Import ReactDOM for portals
import { Loader2, Send, ChevronDown } from 'lucide-react'; // Updated: add new icons
import { fetchPermittedModels } from '../../utils/fetchSubscription';

const ChatInput = ({ chatInput, setChatInput, handleChatSubmit, isLoading, inputRef: externalInputRef, onHeightChange, uid, permittedModels }) => {
  // Use provided ref if given, otherwise create internal
  const internalRef = useRef(null);
  const inputRef = externalInputRef || internalRef;
  const wrapperRef = useRef(null); // NEW: measure total input area height
  const [justClicked, setJustClicked] = useState(false);
  const [isModelDropdownOpen, setModelDropdownOpen] = useState(false); // New: state for custom dropdown
  const modelDropdownRef = useRef(null); // New: ref for dropdown
  const modelButtonRef = useRef(null); // New: ref for the dropdown trigger button
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // New: state for dropdown position
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  const modelMap = useMemo(() => ({
    'gemini-2.5-flash-lite': 'Gemini 2.5 Flash Lite',
    'grok-3-mini': 'Grok-3-mini',
    'gemini-2.5-flash': 'Gemini 2.5 Flash',
    'gpt-5-mini': 'GPT-5-mini',
    'gemini-2.5-pro': 'Gemini 2.5 Pro',
    'gpt-5': 'GPT-5'
  }), []);

  const orderedKeys = useMemo(() => [
    'gemini-2.5-flash-lite',
    'grok-3-mini',
    'gemini-2.5-flash',
    'gpt-5-mini',
    'gemini-2.5-pro',
    'gpt-5'
  ], []);

  const [selectedModelKey, setSelectedModelKey] = useState('gemini-2.5-flash-lite');

  // Fetch or use provided models
  useEffect(() => {
    if (Array.isArray(permittedModels) && permittedModels.length) {
      setAvailableModels(permittedModels.filter(Boolean));
      setModelsLoading(false);
      return;
    }
    if (!uid) {
      setModelsLoading(false);
      return;
    }
    let active = true;
    setModelsLoading(true);
    (async () => {
      try {
        const models = await fetchPermittedModels(uid);
        if (!active) return;
        if (Array.isArray(models) && models.length) {
          setAvailableModels(models.filter(Boolean));
        } else {
          setAvailableModels([]);
        }
      } catch (e) {
        if (active) {
          setAvailableModels([]);
        }
      } finally {
        if (active) setModelsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [uid, permittedModels]);

  // Validate selection after models load
  useEffect(() => {
    if (!availableModels.length) return;
    if (!availableModels.includes(selectedModelKey)) {
      const first = orderedKeys.find(k => availableModels.includes(k));
      if (first) setSelectedModelKey(first);
    }
  }, [availableModels, selectedModelKey, orderedKeys]);

  // Auto-resize up to a cap (memoized to satisfy eslint)
  const autoResize = useCallback(() => {
    const ta = inputRef.current;
    if (!ta) return;
    const max = 240; // px cap
    ta.style.height = 'auto';
    const newHeight = Math.min(ta.scrollHeight, max);
    ta.style.height = `${newHeight}px`;
    // If content taller than cap, allow internal scrolling and ensure bottom is visible
    if (ta.scrollHeight > max) {
      ta.style.overflowY = 'auto';
      // scroll internal textarea viewport to bottom so the last line is visible
      ta.scrollTop = ta.scrollHeight;
    } else {
      ta.style.overflowY = 'hidden';
      ta.scrollTop = 0;
    }

    // Notify parent of wrapper height (measure after layout settle)
    requestAnimationFrame(() => {
      const h = wrapperRef.current ? wrapperRef.current.offsetHeight : 0;
      if (typeof onHeightChange === 'function') onHeightChange(h);
    });
  }, [inputRef, onHeightChange]);

  useEffect(() => {
    autoResize();
  }, [chatInput, autoResize, isLoading]);

  useEffect(() => {
    // also measure on mount
    requestAnimationFrame(() => {
      const h = wrapperRef.current ? wrapperRef.current.offsetHeight : 0;
      if (typeof onHeightChange === 'function') onHeightChange(h);
    });
    // update on window resize
    const handleResize = () => {
      requestAnimationFrame(() => {
        const h = wrapperRef.current ? wrapperRef.current.offsetHeight : 0;
        if (typeof onHeightChange === 'function') onHeightChange(h);
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onHeightChange]);

  // New: Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the button and the dropdown itself
      if (
        modelButtonRef.current && !modelButtonRef.current.contains(event.target) &&
        modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)
      ) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // New: Function to calculate and set dropdown position
  const calculateDropdownPosition = useCallback(() => {
    if (modelButtonRef.current) {
      const rect = modelButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 10, // Position above the button with a small gap
        left: rect.left,
      });
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setJustClicked(true);
      setTimeout(() => setJustClicked(false), 400);
      handleChatSubmit(e, selectedModelKey); // Updated: pass selected model key
      // If parent clears chatInput on submit, height will reset via effect
    }
  };

  // Updated to prevent default (avoid page reload)
  const onSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return; // Prevent submission if input is empty
    setJustClicked(true);
    setTimeout(() => setJustClicked(false), 400);
    handleChatSubmit(e, selectedModelKey); // Updated: pass selected model key
  };

  // keep a global fallback in case parent doesn't forward selectedModel
  useEffect(() => {
    try {
      window.__SELECTED_AIMODEL = selectedModelKey;
    } catch (e) {
      // noop in non-browser env
    }
  }, [selectedModelKey]);

  return (
    // Attach wrapperRef to form so parent gets full input area height
    <form ref={wrapperRef} onSubmit={onSubmit} className="mt-4 mx-2 border-t pt-4">
      <div className="mb-4">
        {/* Updated: Restructured layout */}
        <div className={`relative w-full p-3 border-2 rounded-xl bg-gray-50 hover:bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-colors duration-200 ${isLoading ? 'opacity-70' : ''}`}>
          {/* New: slim loading bar */}
          {isLoading && <div className="chatinput-progress" />}
          
          <textarea
            ref={inputRef}
            rows="2"
            className="flex-grow w-full bg-transparent outline-none resize-none overflow-y-auto"
            placeholder="Ask any question"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onInput={autoResize}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            style={{ minHeight: '28px', maxHeight: '240px' }} // Adjusted min-height
          />

          {/* New: Bottom controls row */}
          <div className="flex justify-between items-center mt-2">
            {/* Left controls */}
            <div className="flex items-center gap-2">
              {/* Updated: Custom styled model selector dropdown */}
              <div className="relative">
                <button
                  ref={modelButtonRef} // Attach ref to the button
                  type="button"
                  onClick={() => {
                    calculateDropdownPosition(); // Calculate position before opening
                    setModelDropdownOpen(!isModelDropdownOpen);
                  }}
                  className="flex items-center bg-transparent text-sm font-medium text-gray-600 hover:text-black focus:outline-none cursor-pointer"
                  disabled={isLoading}
                >
                  <span>{modelMap[selectedModelKey] || selectedModelKey}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 ml-1 transition-transform duration-200 ${isModelDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isModelDropdownOpen && ReactDOM.createPortal(
                  <div
                    ref={modelDropdownRef}
                    style={{
                      position: 'fixed',
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      transform: 'translateY(-100%)', // Shift it up to align bottom edge
                    }}
                    className="w-60 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                  >
                    <div className="p-1 max-h-72 overflow-y-auto">
                      {modelsLoading ? (
                        <div className="px-3 py-3 text-xs text-gray-500">Loading models...</div>
                      ) : availableModels.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-gray-500">No models available. Upgrade plan or try again.</div>
                      ) : (
                        orderedKeys.map(key => {
                          const label = modelMap[key];
                          const allowed = availableModels.includes(key);
                          const active = key === selectedModelKey;
                          
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => {
                                if (allowed) {
                                  setSelectedModelKey(key);
                                  setModelDropdownOpen(false);
                                }
                              }}
                              className={`w-full text-left px-3 py-2 text-sm rounded-md flex justify-between items-center group 
                                ${allowed ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'}
                                ${active ? 'ring-1 ring-blue-500 bg-blue-50' : ''}`}
                              disabled={!allowed}
                            >
                              <span className="flex items-center gap-2">
                                {label}
                              </span>
                              <div className="flex gap-1 ml-4">
                                {!allowed && (
                                  <span className="text-xs font-semibold text-white bg-blue-500 rounded-full px-2 py-0.5">
                                    Upgrade
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>,
                  document.body // Render into the body
                )}
              </div>
            </div>

            {/* Right controls */}
            <div className="flex items-center space-x-2 ml-2">
              <button
                type="submit"
                className={`relative w-10 h-10 flex items-center justify-center text-white rounded-full shadow-md transition-all duration-200 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black ${
                  chatInput.trim() && availableModels.includes(selectedModelKey) ? 'bg-black hover:bg-gray-800' : 'bg-gray-400 cursor-not-allowed'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoading || !chatInput.trim() || !availableModels.includes(selectedModelKey)}
              >
                {/* Ripple on click */}
                {justClicked && <span className="send-ripple" />}
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ChatInput;
